import { useEffect, useRef, useState } from 'react';

export type MeetingJoinMedia = { microphone: boolean; microphoneDeviceId: string };

/** Selecting a microphone never starts capture. Only Join passes the choice to the room. */
export function MeetingPrejoin({ allowed, busy, onJoin }: {
  allowed: boolean;
  busy: boolean;
  onJoin: (media: MeetingJoinMedia) => void | Promise<void>;
}) {
  const [microphone, setMicrophone] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [joining, setJoining] = useState(false);
  const mounted = useRef(false);
  const pending = useRef(false);

  useEffect(() => {
    mounted.current = true;
    void navigator.mediaDevices?.enumerateDevices().then(list => {
      if (mounted.current) setDevices(list.filter(device => device.kind === 'audioinput'));
    }).catch(() => undefined);
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => { if (!allowed) setMicrophone(false); }, [allowed]);

  async function join() {
    if (!allowed || busy || pending.current) return;
    pending.current = true;
    setJoining(true);
    try { await onJoin({ microphone, microphoneDeviceId: deviceId }); }
    finally {
      pending.current = false;
      if (mounted.current) { setJoining(false); setMicrophone(false); }
    }
  }

  return <section className="meeting-prejoin" aria-label="Before joining the meeting">
    <h2>Ready to join audio?</h2>
    <p>Your microphone starts off. Choose whether to enable it when you join.</p>
    <div className="meeting-actions">
      <button type="button" className="btn" disabled={!allowed || busy || joining} aria-pressed={microphone} aria-label={microphone ? 'Turn microphone off' : 'Turn microphone on'} onClick={() => setMicrophone(value => !value)}>Microphone {microphone ? 'on' : 'off'}</button>
      <label>Microphone<select value={deviceId} disabled={!allowed || busy || joining} onChange={event => setDeviceId(event.target.value)}><option value="">Default microphone</option>{devices.map((device, index) => <option value={device.deviceId} key={device.deviceId || index}>{device.label || `Microphone ${index + 1}`}</option>)}</select></label>
    </div>
    <p className="muted small">Microphone access is requested only when you join with it on.</p>
    {!allowed && <p role="status">Acknowledge the meeting disclosure above to enable joining.</p>}
    <button type="button" className="btn btn-primary" disabled={!allowed || busy || joining} onClick={() => void join()}>{busy || joining ? 'Joining…' : 'Join now'}</button>
  </section>;
}
